import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getButtonNotifyMode, getButtonsWithNotifyClick } from '../../../base/config/functions.web';
import { isMobileBrowser } from '../../../base/environment/utils';
import { getWindowWidth } from '../../../chat/interpreterChatDock';
import HandoffBanner, { MOVE_KEY_WIDE_PLACES } from '../../../device-handoff/components/HandoffBanner';
import { translate } from '../../../base/i18n/functions';
import P2PInterpretingButton from './P2PInterpretingButton';
import InterpreterToolsButton from '../../../interpreter-tools/components/web/InterpreterToolsButton';
import { canRequestP2PInterpreter } from '../../../interpreter-queue/p2pInterpreterEligibility';
import { isReactionsButtonEnabled, shouldDisplayReactionsButtons } from '../../../reactions/functions.web';
import { setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered, showToolbox } from '../../actions.web';
import { callActionSlots, callBarPlaces, hasClientCallActions, isCompactCallBar, splitClientCallBar } from '../../clientCallActions.web';
import { NOT_APPLICABLE, THRESHOLDS } from '../../constants';
import { getAllToolboxButtons, getJwtDisabledButtons, isButtonEnabled, isToolboxVisible } from '../../functions.web';
import { useKeyboardShortcuts } from '../../hooks.web';
import GroupCallHangupButton from './GroupCallHangupButton';
import OverflowMenuButton from './OverflowMenuButton';
import Separator from './Separator';
/**
 * At this window width and below, the open chat sits under the video
 * (css/_chat.scss), not beside it: the bar keeps the window's width, where
 * clientWidth (base/responsive-ui) takes the chat's width off.
 */
const CHAT_UNDER_VIDEO_MAX_WIDTH = 580;
const useStyles = makeStyles()(() => {
    return {
        clientCallActions: {
            // Six 48px targets, five 4px gaps and 12px padding fit a 320px phone.
            // Logical margins: the bar mirrors in a right-to-left interface.
            '&& > div': { marginInlineEnd: 4 },
            '&& > div:last-of-type': { marginInlineEnd: 0 },
            '&& .toolbox-icon': { width: 48, height: 48 }
        },
        contextMenu: {
            position: 'relative',
            right: 'auto',
            margin: 0,
            marginBottom: '8px',
            maxHeight: 'calc(100dvh - 100px)',
            minWidth: '240px'
        },
    };
});
const Toolbox = ({ _clientCallActions, _interpreterTools, _compactCallActions, _moveDevice, _p2pInterpreter, _buttonsWithNotifyClick, _chatOpen, _clientWidth, _customToolbarButtons, _dialog, _disabled, _hangupMenuVisible, _isMobile, _isNarrowLayout, _jwtDisabledButtons, _overflowDrawer, _overflowMenuVisible, _reactionsButtonEnabled, _shiftUp, _shouldDisplayReactionsButtons, _toolbarButtons, _visible, dispatch, t, toolbarButtons }) => {
    const { classes, cx } = useStyles();
    const _toolboxRef = useRef(null);
    useKeyboardShortcuts(toolbarButtons);
    useEffect(() => {
        if (!_visible) {
            if (document.activeElement instanceof HTMLElement
                && _toolboxRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
    }, [_visible]);
    /**
     * Sets the visibility of the hangup menu.
     *
     * @param {boolean} visible - Whether or not the hangup menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, []);
    /**
     * Sets the visibility of the overflow menu.
     *
     * @param {boolean} visible - Whether or not the overflow menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetOverflowVisible = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, []);
    useEffect(() => {
        if (_hangupMenuVisible && !_visible) {
            onSetHangupVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [_hangupMenuVisible, _visible]);
    useEffect(() => {
        if (_overflowMenuVisible && _dialog) {
            onSetOverflowVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [_overflowMenuVisible, _dialog]);
    /**
     * Key handler for overflow/hangup menus.
     *
     * @param {KeyboardEvent} e - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            _hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            _overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
        }
    }, [_hangupMenuVisible, _overflowMenuVisible]);
    /**
     * Sets the notify click mode for the buttons.
     *
     * @param {Object} buttons - The list of toolbar buttons.
     * @returns {void}
     */
    function setButtonsNotifyClickMode(buttons) {
        if (typeof APP === 'undefined' || !_buttonsWithNotifyClick?.length) {
            return;
        }
        Object.values(buttons).forEach((button) => {
            if (typeof button === 'object') {
                button.notifyMode = getButtonNotifyMode(button.key, _buttonsWithNotifyClick);
            }
        });
    }
    /**
     * Returns all buttons that need to be rendered.
     *
     * @param {Object} state - The redux state.
     * @returns {Object} The visible buttons arrays .
     */
    function getVisibleButtons() {
        const buttons = getAllToolboxButtons(_customToolbarButtons);
        setButtonsNotifyClickMode(buttons);
        const isHangupVisible = isButtonEnabled('hangup', _toolbarButtons);
        // Reserve room for the dedicated client Transfer action, or for the
        // interpreter's Tools key (a label or live timer widens it; a narrow
        // bar makes it an icon key).
        const reserved = (_clientCallActions ? 48 : 0) + (_interpreterTools ? _compactCallActions ? 52 : 120 : 0);
        const { order } = THRESHOLDS.find(({ width }) => _clientWidth - reserved > width)
            || THRESHOLDS[THRESHOLDS.length - 1];
        const keys = Object.keys(buttons);
        // A signed-in client's call reads Mic · Camera · CC · Chat · Invite
        // (Request Interpreter is pinned after Mic and Camera below); the rest
        // go to More. Captions come before anything else because for a Deaf
        // caller they are the call.
        const priority = _clientCallActions ? ['microphone', 'camera', 'cc', 'chat', 'invite'] : order;
        const filtered = [
            ...priority.map(key => buttons[key]).filter(Boolean),
            ...Object.values(buttons).filter((_button, index) => !priority.includes(keys[index]))
        ].filter(({ key, alias = NOT_APPLICABLE }) => !_jwtDisabledButtons.includes(key)
            && (isButtonEnabled(key, _toolbarButtons) || isButtonEnabled(alias, _toolbarButtons)));
        let sliceIndex = _overflowDrawer || _reactionsButtonEnabled ? order.length + 2 : order.length + 1;
        if (isHangupVisible) {
            sliceIndex -= 1;
        }
        // This implies that the overflow button will be displayed, so save some space for it.
        if (sliceIndex < filtered.length) {
            sliceIndex -= 1;
        }
        if (_clientCallActions) {
            // Labelled call actions take their real width: Request Interpreter
            // about four keys, the caller's Interpreter three; one key each on
            // a narrow bar, where they are icon keys. The two never show
            // together: the queue request hides for whoever placed a P2P or
            // group call, the only person the green button shows for.
            const pinnedRequest = _clientWidth >= 380 && filtered.some(({ key }) => key === 'requestInterpreter');
            const labelled = _p2pInterpreter
                ? callActionSlots(_compactCallActions, 3)
                : pinnedRequest ? callActionSlots(_compactCallActions, 4) : 0;
            const actions = filtered.filter(({ key }) => !pinnedRequest || key !== 'requestInterpreter');
            // Hang-up always, and Move only while another signed-in device can
            // take the call. Move names the device ("Move to Jane's iPhone")
            // where the name fits beside every action; else it is an icon key,
            // the name in its aria-label and title.
            const moveLabelled = _moveDevice && !_compactCallActions
                && actions.length <= callBarPlaces(_clientWidth) - 1 - labelled - MOVE_KEY_WIDE_PLACES;
            const move = _moveDevice ? moveLabelled ? MOVE_KEY_WIDE_PLACES : 1 : 0;
            // Robert, 2026-10-29: when every action fits the bar, all show and
            // More does not; Schedule for later too. Mic and Camera stay first
            // and End last. A narrow bar keeps Mic, Camera and CC (Mic and
            // Camera beside the green button: a P2P or group room has no
            // interpreted captions), and More holds the rest, Schedule last.
            const { bar, more } = splitClientCallBar(actions, {
                fixedPlaces: 1 + labelled + move,
                limit: sliceIndex,
                minimum: _p2pInterpreter ? 2 : 3,
                moreOnly: ({ key }) => key === 'scheduleInterpreter',
                width: _clientWidth
            });
            // The pinned request renders in its own place after Mic and Camera
            // (renderToolboxContent takes it out of both lists), never in More.
            return {
                mainMenuButtons: bar,
                moveLabelled,
                overflowMenuButtons: [...more, ...filtered.filter(({ key }) => pinnedRequest && key === 'requestInterpreter')]
            };
        }
        // Everyone else: Schedule for later is a client action, in More.
        const moreOnly = filtered.filter(({ key }) => key === 'scheduleInterpreter');
        const barCandidates = filtered.filter(({ key }) => key !== 'scheduleInterpreter');
        return {
            mainMenuButtons: barCandidates.slice(0, sliceIndex),
            moveLabelled: false,
            overflowMenuButtons: [...barCandidates.slice(sliceIndex), ...moreOnly]
        };
    }
    /**
     * Dispatches an action signaling the toolbar is not being hovered.
     *
     * @private
     * @returns {void}
     */
    function onMouseOut() {
        !_overflowMenuVisible && dispatch(setToolbarHovered(false));
    }
    /**
     * Dispatches an action signaling the toolbar is being hovered.
     *
     * @private
     * @returns {void}
     */
    function onMouseOver() {
        dispatch(setToolbarHovered(true));
    }
    /**
     * Toggle the toolbar visibility when tabbing into it.
     *
     * @returns {void}
     */
    const onTabIn = useCallback(() => {
        if (!_visible) {
            dispatch(showToolbox());
        }
    }, [_visible]);
    /**
     * Renders the toolbox content.
     *
     * @returns {ReactElement}
     */
    function renderToolboxContent() {
        const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
        const containerClassName = `toolbox-content${_isMobile || _isNarrowLayout ? ' toolbox-content-mobile' : ''}`;
        const { mainMenuButtons, moveLabelled, overflowMenuButtons } = getVisibleButtons();
        const requestInterpreterButton = (!_clientCallActions || _clientWidth >= 380)
            ? [...mainMenuButtons, ...overflowMenuButtons].find(({ key }) => key === 'requestInterpreter')
            : undefined;
        const mainToolbarButtons = mainMenuButtons.filter(({ key }) => !requestInterpreterButton || key !== 'requestInterpreter');
        const overflowToolbarButtons = overflowMenuButtons.filter(({ key }) => !requestInterpreterButton || key !== 'requestInterpreter');
        const primaryButtonCount = Math.min(2, mainToolbarButtons.length);
        const primaryToolbarButtons = mainToolbarButtons.slice(0, primaryButtonCount);
        const secondaryToolbarButtons = mainToolbarButtons.slice(primaryButtonCount);
        const raiseHandInOverflowMenu = overflowToolbarButtons.some(({ key }) => key === 'raisehand');
        const showReactionsInOverflowMenu = _shouldDisplayReactionsButtons
            && ((!_reactionsButtonEnabled && (raiseHandInOverflowMenu || _isNarrowLayout || _isMobile))
                || overflowToolbarButtons.some(({ key }) => key === 'reactions'));
        const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
        const renderToolbarButton = ({ Content, key, ...rest }) => Content !== Separator && (React.createElement(Content, { ...rest, buttonKey: key, key: key }));
        return (React.createElement("div", { className: containerClassName },
            React.createElement("div", { className: 'toolbox-content-wrapper', onFocus: onTabIn, ...(_isMobile ? {} : {
                    onMouseOut,
                    onMouseOver
                }) },
                React.createElement("div", { className: cx('toolbox-content-items', _clientCallActions && classes.clientCallActions), ref: _toolboxRef },
                    primaryToolbarButtons.map(renderToolbarButton),
                    requestInterpreterButton && renderToolbarButton(requestInterpreterButton),
                    React.createElement(P2PInterpretingButton, null),
                    React.createElement(InterpreterToolsButton, null),
                    secondaryToolbarButtons.map(renderToolbarButton),
                    _clientCallActions && React.createElement(HandoffBanner, { compact: !moveLabelled }),
                    Boolean(overflowToolbarButtons.length) && (React.createElement(OverflowMenuButton, { ariaControls: 'overflow-menu', buttons: overflowToolbarButtons.reduce((acc, val) => {
                            if (val.key === 'reactions' && showReactionsInOverflowMenu) {
                                return acc;
                            }
                            if (val.key === 'raisehand' && showRaiseHandInReactionsMenu) {
                                return acc;
                            }
                            if (acc.length) {
                                const prev = acc[acc.length - 1];
                                const group = prev[prev.length - 1].group;
                                if (group === val.group) {
                                    prev.push(val);
                                }
                                else {
                                    acc.push([val]);
                                }
                            }
                            else {
                                acc.push([val]);
                            }
                            return acc;
                        }, []), isOpen: _overflowMenuVisible, key: 'overflow-menu', onToolboxEscKey: onEscKey, onVisibilityChange: onSetOverflowVisible, showRaiseHandInReactionsMenu: showRaiseHandInReactionsMenu, showReactionsMenu: showReactionsInOverflowMenu })),
                    isButtonEnabled('hangup', _toolbarButtons) && (React.createElement(GroupCallHangupButton, { endNotifyMode: getButtonNotifyMode('end-meeting', _buttonsWithNotifyClick), inDrawer: _overflowDrawer, isOpen: _hangupMenuVisible, key: 'hangup-button', menuNotifyMode: getButtonNotifyMode('hangup-menu', _buttonsWithNotifyClick), notifyMode: getButtonNotifyMode('hangup', _buttonsWithNotifyClick), onEscKey: onEscKey, onVisibilityChange: onSetHangupVisible }))))));
    }
    if (_disabled) {
        return null;
    }
    const rootClassNames = `new-toolbox ${_visible ? 'visible' : ''} ${_toolbarButtons.length ? '' : 'no-buttons'} ${_chatOpen ? 'shift-right' : ''}`;
    return (React.createElement("div", { className: cx(rootClassNames, _shiftUp && 'shift-up'), id: 'new-toolbox' }, renderToolboxContent()));
};
/**
 * Maps (parts of) the redux state to {@link Toolbox}'s React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @param {Object} ownProps - The props explicitly passed.
 * @private
 * @returns {{}}
 */
function _mapStateToProps(state, ownProps) {
    const { isNarrowLayout } = state['features/base/responsive-ui'];
    const { customToolbarButtons, iAmRecorder, iAmSipGateway } = state['features/base/config'];
    const { hangupMenuVisible, overflowMenuVisible, overflowDrawer } = state['features/toolbox'];
    const { clientWidth } = state['features/base/responsive-ui'];
    const toolbarButtons = ownProps.toolbarButtons || state['features/toolbox'].toolbarButtons;
    const windowWidth = getWindowWidth();
    const chatUnderVideo = Boolean(state['features/chat'].isOpen) && windowWidth > 0
        && windowWidth <= CHAT_UNDER_VIDEO_MAX_WIDTH;
    const clientCallActions = hasClientCallActions(state);
    return {
        _clientCallActions: clientCallActions,
        _interpreterTools: Boolean(state['features/interpreter-tools']?.mounted),
        _compactCallActions: isCompactCallBar(state),
        _moveDevice: clientCallActions && Boolean(state['features/device-handoff']?.companionDevices?.length),
        _p2pInterpreter: clientCallActions && canRequestP2PInterpreter(),
        _buttonsWithNotifyClick: getButtonsWithNotifyClick(state),
        _chatOpen: state['features/chat'].isOpen,
        _clientWidth: chatUnderVideo ? windowWidth : clientWidth,
        _customToolbarButtons: customToolbarButtons,
        _dialog: Boolean(state['features/base/dialog'].component),
        _disabled: Boolean(iAmRecorder || iAmSipGateway),
        _isMobile: isMobileBrowser(),
        _jwtDisabledButtons: getJwtDisabledButtons(state),
        _hangupMenuVisible: hangupMenuVisible,
        _isNarrowLayout: isNarrowLayout,
        _overflowMenuVisible: overflowMenuVisible,
        _overflowDrawer: overflowDrawer,
        _reactionsButtonEnabled: isReactionsButtonEnabled(state),
        _shiftUp: state['features/toolbox'].shiftUp,
        _shouldDisplayReactionsButtons: shouldDisplayReactionsButtons(state),
        _toolbarButtons: toolbarButtons,
        _visible: isToolboxVisible(state)
    };
}
export default translate(connect(_mapStateToProps)(Toolbox));
